import {
  Building2,
  HardHat,
  Shield,
  CheckCircle,
  Users,
  ArrowRight,
  Cpu,
  Phone,
  Headphones,
  TrendingUp,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import VHLogo from '../components/VHLogo';

export default function Pricing() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [scrolled, setScrolled] = useState(false);
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [animatedSavings, setAnimatedSavings] = useState(0);
  const [animatedReduction, setAnimatedReduction] = useState(0);
  const [animatedTimeSavings, setAnimatedTimeSavings] = useState(0);

  // Map Tailwind color classes statically to avoid dynamic class generation issues in production
  const colorClasses = {
    blue: { overlay: 'from-blue-500/10 to-blue-500/5', icon: 'from-blue-500 to-blue-600' },
    orange: {
      overlay: 'from-orange-500/10 to-orange-500/5',
      icon: 'from-orange-500 to-orange-600',
    },
    purple: {
      overlay: 'from-purple-500/10 to-purple-500/5',
      icon: 'from-purple-500 to-purple-600',
    },
    green: { overlay: 'from-green-500/10 to-green-500/5', icon: 'from-green-500 to-green-600' },
  };
  const getColor = (color) => colorClasses[color] || colorClasses.orange;

  // Handle scroll effect for header
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animate ROI numbers
  useEffect(() => {
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        setAnimatedSavings((prev) => Math.min(prev + 2500, 125000));
        setAnimatedReduction((prev) => Math.min(prev + 1.5, 60));
        setAnimatedTimeSavings((prev) => Math.min(prev + 1.8, 75));
      }, 50);
      setTimeout(() => clearInterval(interval), 3000);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const pricingPlans = [
    {
      id: 'gc',
      name: 'General Contractor',
      description: 'Enterprise oversight for multi-project operations',
      price: { monthly: 'Free', annual: 'Free' },
      subtitle: 'When mandating Virtual Hardhat across projects',
      icon: <Building2 className="h-8 w-8" />,
      color: 'blue',
      popular: false,
      features: [
        'Unlimited construction projects',
        'Unlimited subcontractor onboarding',
        'Advanced multi-project analytics',
        'Enterprise compliance dashboards',
        'Subcontractor performance monitoring',
        'Risk assessment across all sites',
        'Priority email and phone support',
        'Dedicated customer success manager',
        'API access and webhooks',
        'Custom reporting and exports',
        'Enterprise SSO integration',
        'Advanced user role management',
      ],
      technicalSpecs: [
        '99.99% uptime SLA',
        'Enterprise-grade security',
        'Multi-region data hosting',
        'Advanced audit logging',
        'Custom integrations available',
        'Dedicated infrastructure',
      ],
      cta: 'Get Started Free',
      link: '/register-gc',
    },
    {
      id: 'sc',
      name: 'Subcontractor',
      description: 'Complete crew management and safety tracking',
      price: { monthly: '$12', annual: '$10' },
      subtitle: 'per user per month',
      icon: <HardHat className="h-8 w-8" />,
      color: 'orange',
      popular: true,
      features: [
        'Mobile crew check-in system',
        'Digital safety certifications',
        'Real-time incident reporting',
        'Automated timesheet integration',
        'Safety training management',
        'Equipment inspection tracking',
        'Compliance score monitoring',
        'Weather alerts and notifications',
        'Photo documentation tools',
        'Offline mode capability',
        'Basic integrations (Procore, etc.)',
        'Email and chat support',
      ],
      technicalSpecs: [
        'Mobile-first responsive design',
        '99.9% uptime guarantee',
        'Real-time data synchronization',
        'Offline data persistence',
        'Push notifications',
        'Biometric authentication',
      ],
      cta: 'Start Free Trial',
      link: '/register-sc',
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Custom solutions for large-scale operations',
      price: { monthly: 'Custom', annual: 'Custom' },
      subtitle: 'Tailored to your organization',
      icon: <Shield className="h-8 w-8" />,
      color: 'purple',
      popular: false,
      features: [
        'Everything in GC + SC plans',
        'Custom AI model training',
        'Dedicated cloud infrastructure',
        'Advanced analytics and BI',
        'Custom integration development',
        'White-label platform options',
        'Dedicated support team',
        '24/7 phone and chat support',
        'Custom SLA agreements',
        'On-premise deployment options',
        'Advanced security features',
        'Custom training and onboarding',
      ],
      technicalSpecs: [
        'Custom SLA up to 99.99%',
        'Dedicated infrastructure',
        'Custom security controls',
        'Advanced audit capabilities',
        'Custom data retention',
        'Compliance consultation',
      ],
      cta: 'Contact Sales',
      link: '/contact',
    },
    {
      id: 'broker',
      name: 'Insurance Broker',
      description: 'Risk assessment and portfolio management',
      price: { monthly: '$25', annual: '$20' },
      subtitle: 'per portfolio per month',
      icon: <TrendingUp className="h-8 w-8" />,
      color: 'green',
      popular: false,
      features: [
        'Multi-client portfolio dashboard',
        'Advanced risk analytics',
        'Predictive risk modeling',
        'Client safety scoring',
        'Historical incident analysis',
        'Insurance optimization tools',
        'Premium calculation assistance',
        'Risk trend forecasting',
        'Client benchmarking reports',
        'Automated risk alerts',
        'Custom reporting suite',
        'Dedicated broker support',
      ],
      technicalSpecs: [
        'Multi-tenant architecture',
        'Advanced data analytics',
        'Real-time risk scoring',
        'Predictive ML models',
        'Custom report generation',
        'API access for integrations',
      ],
      cta: 'Start Broker Trial',
      link: '/register-broker',
    },
  ];

  const addOnServices = [
    {
      name: 'Advanced AI Training',
      description: 'Custom machine learning model training on your historical safety data',
      price: '$5,000',
      period: 'one-time setup',
    },
    {
      name: 'Custom Integrations',
      description: 'Bespoke API integrations with your existing construction management systems',
      price: '$2,500',
      period: 'per integration',
    },
    {
      name: 'Dedicated Success Manager',
      description: '1:1 customer success support with quarterly business reviews',
      price: '$1,200',
      period: 'per month',
    },
    {
      name: 'On-Premise Deployment',
      description: 'Private cloud or on-premise infrastructure with dedicated support',
      price: '$15,000',
      period: 'setup + $3,000/mo hosting',
    },
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Global Space Grotesk font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
        * {
          font-family: "Space Grotesk", system-ui, sans-serif !important;
        }
      `}</style>

      {/* Header */}
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled ? 'bg-gray-900/95 backdrop-blur-xl border-b border-gray-800' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/">
              <VHLogo />
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link
                to="/features"
                className="text-gray-300 hover:text-white font-medium transition-colors"
              >
                Features
              </Link>
              <Link to="/pricing" className="text-orange-400 font-medium">
                Pricing
              </Link>
              <Link
                to="/contact"
                className="text-gray-300 hover:text-white font-medium transition-colors"
              >
                Contact
              </Link>

              <div className="flex items-center space-x-4 ml-8 pl-8 border-l border-gray-700">
                <Link
                  to="/login"
                  className="text-gray-300 hover:text-white font-medium transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-500/25"
                >
                  Get Started
                </Link>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Animated Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Dynamic background */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Animated gradient orbs */}
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute w-32 h-32 bg-gradient-to-br from-orange-500/10 to-purple-500/10 rounded-full blur-2xl animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${4 + Math.random() * 2}s`,
              }}
            />
          ))}

          {/* Moving grid */}
          <div className="absolute inset-0 opacity-10">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `
                     linear-gradient(rgba(249, 115, 22, 0.2) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(249, 115, 22, 0.2) 1px, transparent 1px)
                   `,
                backgroundSize: '50px 50px',
                animation: 'gridFloat 30s linear infinite',
              }}
            ></div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center relative">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 animate-slideDown">
            Transparent
            <br />
            <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
              Enterprise Pricing
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed animate-slideUp">
            Fair, transparent pricing that scales with your construction operations. No hidden fees,
            no vendor lock-in, no surprises.
          </p>

          {/* Interactive Billing Toggle */}
          <div className="flex items-center justify-center space-x-4 mb-16 animate-fadeIn">
            <span
              className={`font-medium transition-all duration-300 ${billingCycle === 'monthly' ? 'text-white scale-105' : 'text-gray-400'}`}
            >
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              className={`relative inline-flex h-8 w-16 items-center rounded-full transition-all duration-500 hover:scale-110 ${
                billingCycle === 'annual'
                  ? 'bg-orange-500 shadow-lg shadow-orange-500/30'
                  : 'bg-gray-700'
              }`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-all duration-300 ${
                  billingCycle === 'annual' ? 'translate-x-9 scale-110' : 'translate-x-1'
                }`}
              />
            </button>
            <span
              className={`font-medium transition-all duration-300 ${billingCycle === 'annual' ? 'text-white scale-105' : 'text-gray-400'}`}
            >
              Annual
              <span className="text-green-400 text-sm ml-2 animate-pulse">Save 20%</span>
            </span>
          </div>
        </div>

        <style>{`
          @keyframes gridFloat {
            0% { transform: translateX(0) translateY(0) rotate(0deg); }
            100% { transform: translateX(50px) translateY(50px) rotate(1deg); }
          }
          @keyframes slideDown {
            0% { opacity: 0; transform: translateY(-30px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes slideUp {
            0% { opacity: 0; transform: translateY(30px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
          .animate-slideDown {
            animation: slideDown 1s ease-out;
          }
          .animate-slideUp {
            animation: slideUp 1s ease-out 0.3s both;
          }
          .animate-fadeIn {
            animation: fadeIn 1s ease-out 0.6s both;
          }
        `}</style>
      </section>

      {/* Interactive Pricing Plans */}
      <section className="py-20 relative overflow-hidden">
        {/* Floating pricing elements */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-gradient-to-r from-orange-500 to-purple-500 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 4}s`,
                animationDuration: `${2 + Math.random() * 3}s`,
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-8">
            {pricingPlans.map((plan, index) => (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-8 border transition-all duration-700 cursor-pointer group ${
                  plan.popular
                    ? 'bg-gradient-to-b from-orange-500/20 to-transparent border-2 border-orange-500/50 scale-105 shadow-2xl shadow-orange-500/20'
                    : 'bg-gray-900/40 backdrop-blur-sm border-gray-800 hover:border-orange-500/30 hover:scale-110'
                }`}
                style={{
                  animation: `planSlideIn 0.8s ease-out ${index * 0.1}s both`,
                }}
                onMouseEnter={() => setHoveredPlan(plan.id)}
                onMouseLeave={() => setHoveredPlan(null)}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-1 rounded-full text-sm font-semibold animate-bounce shadow-lg">
                    MOST POPULAR
                  </div>
                )}

                {/* Animated background glow */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br via-transparent ${getColor(plan.color).overlay} opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-2xl`}
                ></div>

                {/* Floating icon */}
                <div
                  className={`w-16 h-16 bg-gradient-to-br ${getColor(plan.color).icon} rounded-2xl flex items-center justify-center mb-6 text-white shadow-lg transition-all duration-500 group-hover:scale-125 group-hover:rotate-12 relative overflow-hidden`}
                >
                  <div className="relative z-10">{plan.icon}</div>
                  {/* Rotating glow */}
                  <div
                    className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-spin rounded-2xl"
                    style={{ animationDuration: '3s' }}
                  ></div>
                </div>

                <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors duration-500">
                  {plan.name}
                </h3>
                <p className="text-gray-400 mb-6 group-hover:text-gray-300 transition-colors">
                  {plan.description}
                </p>

                {/* Animated price display */}
                <div className="mb-8">
                  <div className="flex items-baseline mb-2">
                    <span
                      className={`text-4xl font-bold transition-all duration-500 ${
                        hoveredPlan === plan.id ? 'text-orange-400 scale-110' : 'text-white'
                      }`}
                    >
                      {plan.price[billingCycle]}
                    </span>
                    {plan.price[billingCycle] !== 'Free' &&
                      plan.price[billingCycle] !== 'Custom' && (
                        <span className="text-gray-400 ml-2">
                          /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                        </span>
                      )}
                  </div>
                  <p className="text-gray-500 text-sm">{plan.subtitle}</p>

                  {/* Discount indicator */}
                  {billingCycle === 'annual' &&
                    plan.price.monthly !== 'Free' &&
                    plan.price.monthly !== 'Custom' && (
                      <div className="text-green-400 text-xs mt-2 animate-pulse">
                        💰 Save 20% with annual billing
                      </div>
                    )}
                </div>

                {/* Animated feature list */}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-start space-x-3 group-hover:translate-x-1 transition-transform duration-300"
                      style={{ transitionDelay: `${featureIndex * 0.05}s` }}
                    >
                      <CheckCircle
                        className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5 animate-pulse"
                        style={{ animationDelay: `${featureIndex * 0.1}s` }}
                      />
                      <span className="text-gray-300 text-sm group-hover:text-gray-200 transition-colors">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Interactive Technical Specifications */}
                <div className="bg-gray-800/30 rounded-lg p-4 mb-6 group-hover:bg-gray-800/50 transition-all duration-500 transform group-hover:scale-105">
                  <div className="text-white font-medium text-sm mb-3 flex items-center">
                    <Cpu className="h-4 w-4 mr-2 animate-pulse" />
                    Technical Specifications
                  </div>
                  <div className="space-y-1">
                    {plan.technicalSpecs.map((spec, specIndex) => (
                      <div
                        key={specIndex}
                        className="text-xs text-gray-400 group-hover:text-gray-300 transition-all duration-300"
                        style={{ transitionDelay: `${specIndex * 0.1}s` }}
                      >
                        • {spec}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Animated CTA button */}
                <Link
                  to={plan.link}
                  className={`block w-full text-center py-3 rounded-lg font-semibold transition-all duration-500 hover:scale-110 relative overflow-hidden group ${
                    plan.popular
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 shadow-lg shadow-orange-500/25'
                      : plan.id === 'enterprise'
                        ? 'bg-gray-800 text-white hover:bg-gray-700 border border-gray-700'
                        : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
                  }`}
                >
                  <span className="relative z-10">{plan.cta}</span>
                  {/* Animated background shimmer */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                </Link>

                {/* Floating elements for popular plan */}
                {plan.popular && (
                  <>
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-orange-500/50 rounded-full animate-ping"></div>
                    <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-orange-500/30 rounded-full animate-pulse"></div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes planSlideIn {
            0% { 
              opacity: 0; 
              transform: translateY(50px) scale(0.9);
            }
            100% { 
              opacity: 1; 
              transform: translateY(0) scale(1);
            }
          }
        `}</style>
      </section>

      {/* Add-on Services */}
      <section className="py-32 relative">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Professional Services
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Accelerate your deployment and maximize value with our professional services team.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {addOnServices.map((service, index) => (
              <div
                key={index}
                className="bg-gray-900/40 backdrop-blur-sm rounded-2xl p-8 border border-gray-800 hover:border-orange-500/30 transition-all duration-500 group"
              >
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-orange-400 transition-colors">
                  {service.name}
                </h3>
                <p className="text-gray-400 mb-6 leading-relaxed">{service.description}</p>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-orange-400">{service.price}</span>
                    <span className="text-gray-500 text-sm ml-2">{service.period}</span>
                  </div>
                  <button className="text-orange-400 font-medium group-hover:translate-x-2 transition-transform">
                    Learn More →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Support */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-orange-500/5 to-transparent"></div>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
                Enterprise Support
                <br />
                <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                  When You Need It
                </span>
              </h2>

              <p className="text-xl text-gray-300 mb-12 leading-relaxed">
                Dedicated support teams, custom SLA agreements, and enterprise success management
                ensure your construction operations never miss a beat.
              </p>

              <div className="space-y-8">
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center">
                    <Headphones className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-3">24/7 Enterprise Support</h3>
                    <p className="text-gray-400 text-lg">
                      Dedicated support teams available around the clock for enterprise customers
                      with guaranteed response times and escalation procedures.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-3">
                      Customer Success Management
                    </h3>
                    <p className="text-gray-400 text-lg">
                      Dedicated customer success managers provide strategic guidance, quarterly
                      business reviews, and optimization recommendations.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Support Tiers */}
            <div className="space-y-6">
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-bold text-white">Standard Support</h4>
                  <span className="text-green-400 text-sm">Included</span>
                </div>
                <div className="space-y-2 text-sm text-gray-400">
                  <div>• Email support (24-hour response)</div>
                  <div>• Knowledge base access</div>
                  <div>• Community forum</div>
                  <div>• Video tutorials and documentation</div>
                </div>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-800 border-orange-500/30">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-bold text-white">Priority Support</h4>
                  <span className="text-orange-400 text-sm">Enterprise Plans</span>
                </div>
                <div className="space-y-2 text-sm text-gray-400">
                  <div>• Phone and chat support (2-hour response)</div>
                  <div>• Dedicated support engineer</div>
                  <div>• Custom training sessions</div>
                  <div>• Priority feature requests</div>
                </div>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-800 border-purple-500/30">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-bold text-white">Enterprise Support</h4>
                  <span className="text-purple-400 text-sm">Custom Plans</span>
                </div>
                <div className="space-y-2 text-sm text-gray-400">
                  <div>• 24/7 phone support (30-minute response)</div>
                  <div>• Dedicated customer success manager</div>
                  <div>• Custom SLA agreements</div>
                  <div>• On-site training and implementation</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive ROI Calculator */}
      <section className="py-32 relative overflow-hidden">
        {/* Animated value background */}
        <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-transparent to-blue-500/5"></div>

        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">Calculate Your ROI</h2>
          <p className="text-xl text-gray-400 mb-16">
            See how Virtual Hardhat pays for itself through incident prevention and compliance
            automation.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800 hover:border-green-500/50 transition-all duration-500 group hover:scale-110 cursor-pointer">
              {/* Animated counter */}
              <div className="text-3xl font-bold text-green-400 mb-4 group-hover:scale-125 transition-transform duration-500">
                ${animatedSavings.toLocaleString()}
              </div>
              <div className="text-white font-medium mb-2 group-hover:text-green-400 transition-colors">
                Average Annual Savings
              </div>
              <div className="text-gray-500 text-sm">Based on 100-worker operation</div>

              {/* Progress bar */}
              <div className="w-full bg-gray-700 rounded-full h-2 mt-4">
                <div
                  className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${(animatedSavings / 125000) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800 hover:border-blue-500/50 transition-all duration-500 group hover:scale-110 cursor-pointer">
              <div className="text-3xl font-bold text-blue-400 mb-4 group-hover:scale-125 transition-transform duration-500">
                {animatedReduction.toFixed(0)}%
              </div>
              <div className="text-white font-medium mb-2 group-hover:text-blue-400 transition-colors">
                Incident Reduction
              </div>
              <div className="text-gray-500 text-sm">Average across enterprise customers</div>

              <div className="w-full bg-gray-700 rounded-full h-2 mt-4">
                <div
                  className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${(animatedReduction / 60) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800 hover:border-purple-500/50 transition-all duration-500 group hover:scale-110 cursor-pointer">
              <div className="text-3xl font-bold text-purple-400 mb-4 group-hover:scale-125 transition-transform duration-500">
                {animatedTimeSavings.toFixed(0)}%
              </div>
              <div className="text-white font-medium mb-2 group-hover:text-purple-400 transition-colors">
                Compliance Time Savings
              </div>
              <div className="text-gray-500 text-sm">Automated reporting and documentation</div>

              <div className="w-full bg-gray-700 rounded-full h-2 mt-4">
                <div
                  className="bg-gradient-to-r from-purple-400 to-purple-600 h-2 rounded-full transition-all duration-1000"
                  style={{ width: `${(animatedTimeSavings / 75) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="mt-16">
            <Link
              to="/contact"
              className="group inline-flex items-center px-8 py-4 bg-white/5 backdrop-blur-sm border border-white/10 text-white rounded-xl font-semibold text-lg hover:bg-white/10 transition-all hover:scale-110 duration-500 relative overflow-hidden"
            >
              <span className="relative z-10">Calculate Custom ROI</span>
              <ArrowRight className="ml-3 h-5 w-5 relative z-10 group-hover:translate-x-2 transition-transform duration-500" />
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-orange-500/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </Link>
          </div>
        </div>

        <style>{`
          @keyframes planSlideIn {
            0% { 
              opacity: 0; 
              transform: translateY(60px) rotateX(-15deg);
            }
            100% { 
              opacity: 1; 
              transform: translateY(0) rotateX(0deg);
            }
          }
        `}</style>
      </section>

      {/* FAQ Section */}
      <section className="py-32 relative">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Pricing FAQ</h2>
          </div>

          <div className="space-y-8">
            {[
              {
                question: 'Why is the GC plan free?',
                answer:
                  'General contractors who mandate Virtual Hardhat across their projects receive the platform free. This creates network effects where subcontractors join to work with GCs, creating a self-sustaining ecosystem.',
              },
              {
                question: 'What happens if I exceed user limits?',
                answer:
                  'There are no user limits. Our pricing is based on active usage, not seats. Scale your team without worrying about additional per-user costs.',
              },
              {
                question: 'Can I customize my plan?',
                answer:
                  'Absolutely. Enterprise customers can customize their plans with additional features, custom integrations, dedicated infrastructure, and tailored support agreements.',
              },
              {
                question: 'What payment methods do you accept?',
                answer:
                  'We accept all major credit cards, ACH transfers, and wire transfers. Enterprise customers can request invoicing with NET 30 payment terms.',
              },
              {
                question: 'Is there a contract commitment?',
                answer:
                  'No long-term contracts required. Monthly plans can be cancelled anytime. Annual plans offer 20% savings with 12-month commitment. Enterprise plans may include custom terms.',
              },
              {
                question: 'Do you offer discounts for large deployments?',
                answer:
                  'Yes. Contact our sales team for volume discounts on deployments over 500 users or custom pricing for enterprise agreements.',
              },
            ].map((faq, index) => (
              <details
                key={index}
                className="group bg-gray-900/40 backdrop-blur-sm rounded-xl border border-gray-800 hover:border-orange-500/30 transition-all"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer">
                  <h3 className="text-lg font-semibold text-white group-hover:text-orange-400 transition-colors">
                    {faq.question}
                  </h3>
                  <ArrowRight className="h-5 w-5 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-40 relative">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-8">Ready to Get Started?</h2>
          <p className="text-xl text-gray-400 mb-16 max-w-2xl mx-auto">
            Join 500+ construction companies using AI to eliminate safety incidents.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
            <Link
              to="/signup"
              className="group inline-flex items-center justify-center px-12 py-5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold text-xl hover:from-orange-600 hover:to-orange-700 transition-all hover:scale-105 shadow-xl shadow-orange-500/25"
            >
              Start Free Trial
              <ArrowRight className="ml-3 h-6 w-6 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center px-12 py-5 bg-white/5 backdrop-blur-sm border border-white/10 text-white rounded-xl font-semibold text-xl hover:bg-white/10 transition-all hover:scale-105"
            >
              Talk to Sales
              <Phone className="ml-3 h-6 w-6 transition-transform group-hover:scale-110" />
            </Link>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-400 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-400" />
              <span>Free 14-day trial</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-400" />
              <span>No setup fees</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-400" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="md:col-span-1">
              <VHLogo />
              <p className="text-gray-500 mt-4 text-sm">Enterprise AI for construction safety.</p>
            </div>

            <div>
              <h4 className="text-white font-medium mb-4">Platform</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/features" className="text-gray-400 hover:text-white transition-colors">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-gray-400 hover:text-white transition-colors">
                    Security
                  </Link>
                </li>
                <li>
                  <Link
                    to="/integrations"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Integrations
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-medium mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/careers" className="text-gray-400 hover:text-white transition-colors">
                    Careers
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-medium mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                    Terms
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-gray-400 hover:text-white transition-colors">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Virtual Hardhat, Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
